/**
 * The panel between a signature and the record it is about (B1, A2).
 *
 * Signing used to be one click from a list. B1 put a panel in front of
 * it in My Work -- what is being signed, in words, before a button
 * carrying the signer's own name -- and the walk that followed
 * mis-signed nothing. The MVB declaration page still signed on one
 * click, so this is that panel EXTRACTED rather than a second one
 * written: two review panels drift, and the one nobody is looking at
 * drifts first.
 *
 * Two rules live here rather than at the call sites, because a call
 * site can forget them:
 *
 *  - a record with no summary cannot be signed from the panel. The copy
 *    used to say "open it before signing" and let the signature
 *    through, which is how an attestation was approved unseen.
 *  - the panel names the RECORD and its OWNER, not only the signer. A1:
 *    signing somebody else's service otherwise reads exactly like
 *    signing your own.
 */
import type { ReactNode } from "react";
import { Fragment } from "react";
import { Link } from "react-router-dom";

import { termLabel } from "shared/vocab/governance";
import { roleLabel } from "shared/vocab/workflowState";
import { formatDateTime } from "shared/time/formatDate";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

/** A summary line's value in the reader's language.
 *
 *  A term goes through the vocabulary, a sentence we composed through
 *  the catalogue (with the server's English as the fallback), and the
 *  bank's own words -- a rationale, a name -- exactly as written. */
export function summaryValue(line: SummaryLine, t: TFunction): string {
  if (line.is_term) return termLabel(line.value, line.label_key);
  if (line.value_key) {
    const params = { ...(line.params ?? {}) };
    if (params.metricKey) {
      params.metric = t(`toleranceMetrics.metric.${params.metricKey}`, {
        defaultValue: params.metric ?? params.metricKey,
      });
    }
    return t(line.value_key, { ...params, defaultValue: line.value });
  }
  return line.value;
}

export interface SummaryLine {
  label_key: string;
  value: string;
  /** A3 -- one of our closed vocabularies rather than the bank's own
   *  words, so it is read through the catalogue. */
  is_term?: boolean;
  /** Round 14 D3 -- a sentence the catalogue writes, from these. */
  value_key?: string | null;
  params?: Record<string, string> | null;
}

/**
 * Round 11 F1 -- a signature already on the record.
 *
 * Seventh round of asking. A dual sign-off is two people endorsing one
 * decision, and the second was shown that decision with no trace of
 * the first: not the name, not the date, not what they wrote beside
 * it. The business owner countersigning a tolerance could not tell
 * whether the CRO had signed that morning or in March, and the date is
 * most of what decides whether the figure still holds.
 */
export interface PriorSignature {
  role: string;
  name?: string | null;
  at?: string | null;
  note?: string | null;
}

export function ReviewAndSign({
  testid,
  title,
  ownerName,
  summary,
  signedSoFar,
  commitsToKey,
  challengeNote,
  recordLink,
  children,
  onConfirm,
  confirmLabel,
  pending = false,
  onCancel,
  cancelLabel,
}: {
  testid: string;
  title: string;
  ownerName?: string | null | undefined;
  summary: SummaryLine[];
  /** F1 -- who has signed this already. Empty on a first signature,
   *  which is the honest reading: nobody has. */
  signedSoFar?: PriorSignature[];
  commitsToKey?: string | null | undefined;
  challengeNote?: string | null | undefined;
  /** Where the full record lives, for a panel with nothing to show. */
  recordLink: string;
  /** Extra fields a caller needs inside the panel (a comment box). */
  children?: ReactNode;
  onConfirm: () => void;
  confirmLabel: string;
  pending?: boolean;
  onCancel?: () => void;
  cancelLabel?: string;
}) {
  const { t, i18n } = useTranslation();
  const readable = summary.length > 0;
  return (
    <div
      data-testid={`work-sign-panel-${testid}`}
      className="mt-1 ms-3 space-y-2 rounded border border-brand-brass/40 bg-brand-brass/5 p-3"
    >
      <h3 className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-500">
        {t("myWork.sign.heading")}
      </h3>
      <p data-testid={`work-sign-parties-${testid}`} className="text-xs text-slate-700">
        {ownerName
          ? t("myWork.sign.onBehalfOf", { title, owner: ownerName })
          : t("myWork.sign.about", { title })}
      </p>
      {(signedSoFar ?? []).length > 0 && (
        <ul
          data-testid={`work-sign-signed-${testid}`}
          className="space-y-0.5 text-xs text-slate-700"
        >
          {(signedSoFar ?? []).map((sig) => (
            <li key={sig.role} data-testid={`work-sign-signed-${testid}-${sig.role}`}>
              {/* Named, dated, and the reservation where one was
                  recorded. A missing name is reported as missing
                  rather than filled with whoever holds the seat now --
                  that is not who signed. */}
              <span className="font-medium">
                {sig.name
                  ? t("myWork.sign.alreadySigned", {
                      role: roleLabel(sig.role),
                      name: sig.name,
                    })
                  : t("myWork.sign.alreadySignedUnnamed", {
                      role: roleLabel(sig.role),
                    })}
              </span>
              {sig.at ? ` ${formatDateTime(sig.at, i18n.language)}` : ""}
              {sig.note ? ` -- "${sig.note}"` : ""}
            </li>
          ))}
        </ul>
      )}
      {readable ? (
        <dl
          data-testid={`work-sign-summary-${testid}`}
          className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs"
        >
          {/* Round 10 F5 -- keyed by position as well as label.
              Every summary until now had one line per label; a group
              sign-off lists one line PER PACK under the same label, and
              React drops rows that share a key. */}
          {summary.map((line, i) => (
            <Fragment key={`${line.label_key}-${i}`}>
              <dt className="text-slate-500">{t(line.label_key)}</dt>
              <dd className="text-slate-800">{summaryValue(line, t)}</dd>
            </Fragment>
          ))}
        </dl>
      ) : (
        <p
          data-testid={`work-sign-nosummary-${testid}`}
          className="text-xs text-amber-900"
        >
          {t("myWork.sign.noSummary")}{" "}
          <Link to={recordLink} className="font-medium underline">
            {t("myWork.sign.openTheRecord")}
          </Link>
        </p>
      )}
      {challengeNote && (
        /* Round 11 E1 -- a second-line reviewer's own words, in
           whatever language they wrote them, quoted inside a sentence
           in the reader's. Two directions in one paragraph and neither
           was stated: the label resolves the paragraph, and the note
           needs its own run or an Arabic challenge leads an English
           line with its full stop, and an English one does the reverse
           on an Arabic page. */
        <p
          dir="auto"
          data-testid={`work-sign-challenge-${testid}`}
          className="rounded border-s-2 border-rose-300 bg-white px-2 py-1 text-xs text-slate-700"
        >
          <span className="font-semibold">{t("myWork.sign.challenge")}</span>{" "}
          <bdi dir="auto">{challengeNote}</bdi>
        </p>
      )}
      {commitsToKey && <p className="text-xs text-slate-700">{t(commitsToKey)}</p>}
      {children}
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={pending || !readable}
          data-testid={`work-sign-confirm-${testid}`}
          onClick={onConfirm}
          className="rounded bg-brand-brass px-2 py-1 text-xs text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
        >
          {confirmLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            data-testid={`work-sign-cancel-${testid}`}
            onClick={onCancel}
            className="text-xs text-slate-500 hover:underline"
          >
            {cancelLabel ?? t("common.cancel")}
          </button>
        )}
      </div>
    </div>
  );
}
